import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, AlertTriangle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryState, useQueryStates, parseAsInteger, parseAsString, parseAsArrayOf, createParser } from 'nuqs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { VoucherResponse } from '@/app/types/voucher';
import { useOrganization } from '@/app/hooks/useOrganization';
import { listVouchers } from '@/app/services/voucherService';
import { PaginatedResponse } from '@/app/types/api';
import { SortingState } from '@tanstack/react-table';
import { PaymentsOutDataTable } from './components/PaymentsOutDataTable';
import { paymentsOutColumns } from './components/columns';
import { useHotkeys } from 'react-hotkeys-hook';

// Voucher Type ID for 'Payment Made' (PMT)
const PAYMENT_MADE_VOUCHER_TYPE_ID = 3; // Based on constants file

type SortDirection = 'asc' | 'desc';

// Custom parser for sorting state
const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return [{ id: 'transaction_date', desc: true }]
    const [field, direction] = value.split('-')
    return [{
      id: field || 'transaction_date',
      desc: direction === 'desc'
    }]
  },
  serialize: (value: SortingState) => {
    if (!value.length) return 'transaction_date-desc'
    const sort = value[0]
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`
  }
})

// Custom parser for page (0-based internal, 1-based in URL)
const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10)
    return isNaN(page) || page < 1 ? 0 : page - 1 // Convert 1-based to 0-based
  },
  serialize: (value: number) => {
    return String(value + 1) // Convert 0-based to 1-based
  }
})

// Define interface to handle potential variations in pagination response keys
interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  page_count?: number;
  pageCount?: number;
  total_pages?: number;
  totalPages?: number;
}

// Filter configuration object for consistent filtering approach
const FILTER_CONFIG = {
  // Filter format to use: 'array' (with field/value/op objects) or 'object' (simple key-value pairs)
  format: 'array',
  
  // Special field configurations
  fields: {
    'party.name': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%` // Wrap with wildcards for contains search
    },
    'is_cancelled': {
      // Map front-end filter values to backend format
      specialCase: (value: string): { field: string; value: boolean; op: string } | null => {
        if (value === 'active') {
          return { field: 'is_cancelled', value: false, op: 'eq' };
        } else if (value === 'cancelled') {
          return { field: 'is_cancelled', value: true, op: 'eq' };
        }
        return null;
      }
    },
    'voucher_number': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%`
    },
    'reference_number': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%`
    },
    'total_amount': {
      op: 'eq' // Exact match for amounts
    }
  }
};

// Define filter object types
interface FilterObject {
  field: string;
  value: string | number | boolean | string[];
  op: string;
}

// Helper function to build filter parameters based on config
const buildFilterParams = (filters: Record<string, string | number | boolean | { from?: string; to?: string }>, config = FILTER_CONFIG): string => {
  console.log('buildFilterParams called with filters:', filters);
  
  if (config.format === 'array') {
    // Build array of filter objects with field, value, op
    const filterArray: (FilterObject | FilterObject[] | null)[] = Object.entries(filters).map(([field, value]) => {
      // Skip empty values
      if (value === undefined || value === null || value === '') {
        return null;
      }
      
      // Handle date range filters
      if (field === 'transaction_date' && typeof value === 'object' && 'from' in value) {
        const dateFilter = value as { from?: string; to?: string };
        const dateFilters: FilterObject[] = [];
        
        console.log('Processing date filter:', dateFilter);
        
        if (dateFilter.from) {
          // Ensure we add time component for start of day
          const fromDate = `${dateFilter.from} 00:00:00`;
          dateFilters.push({
            field: 'transaction_date',
            value: fromDate,
            op: 'gte' // Greater than or equal
          });
        }
        
        if (dateFilter.to) {
          // Ensure we add time component for end of day
          const toDate = `${dateFilter.to} 23:59:59`;
          dateFilters.push({
            field: 'transaction_date',
            value: toDate,
            op: 'lte' // Less than or equal
          });
        }
        
        console.log('Generated date filters:', dateFilters);
        return dateFilters.length > 0 ? dateFilters : null;
      }
      
      // Check for special case handling
      const fieldConfig = config.fields[field];
      if (fieldConfig?.specialCase) {
        const specialCase = fieldConfig.specialCase(String(value));
        if (specialCase) {
          return specialCase as FilterObject;
        }
      }
      
      // Get actual field name from config or use the original
      const actualField = fieldConfig?.field || field;
      
      // Transform value if needed
      const transformedValue = fieldConfig?.transform ? 
        fieldConfig.transform(String(value)) : 
        value;
      
      // Use configured operator or default to 'eq'
      const op = fieldConfig?.op || 'eq';
      
      return {
        field: actualField,
        value: transformedValue,
        op // Use 'op' instead of 'operator'
      } as FilterObject;
    });
    
    // Flatten the array (to handle date range filters that return arrays)
    const flattenedArray = filterArray.flatMap(f => f || []).filter(Boolean);
    
    const result = JSON.stringify(flattenedArray);
    console.log('Final filter JSON:', result);
    return result;
  } else {
    // Simple object format for exact matches
    const filterObj: Record<string, string | number | boolean> = {};
    
    Object.entries(filters).forEach(([field, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Apply field mapping if configured
        const actualField = config.fields[field]?.field || field;
        filterObj[actualField] = value;
      }
    });
    
    return JSON.stringify(filterObj);
  }
};

const PaymentsOutListPage: React.FC = (): JSX.Element => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const navigate = useNavigate();

  // Use nuqs for URL state management
  const [page, setPage] = useQueryState('page', pageParser.withDefault(0))
  const [search, setSearch] = useQueryState('search', parseAsString.withDefault(''))
  const [sorting, setSorting] = useQueryState('sortBy', sortingParser.withDefault([{ id: 'transaction_date', desc: true }]))
  
  // Use nuqs for filter state management
  const [filterStates, setFilterStates] = useQueryStates({
    voucher_number: parseAsString,
    party_name: parseAsString, // Changed from 'party.name' to avoid URL encoding issues
    reference_number: parseAsString,
    is_cancelled: parseAsString,
    transaction_date_from: parseAsString,
    transaction_date_to: parseAsString,
    total_amount: parseAsString,
  }, {
    // Use shallow routing to prevent full page reloads
    shallow: true,
  })

  // Build filters object from nuqs state
  const filters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }> = {}
  
  // Add simple filters
  if (filterStates.voucher_number) filters.voucher_number = filterStates.voucher_number
  if (filterStates.party_name) filters['party.name'] = filterStates.party_name // Map party_name to party.name
  if (filterStates.reference_number) filters.reference_number = filterStates.reference_number
  if (filterStates.is_cancelled) filters.is_cancelled = filterStates.is_cancelled
  if (filterStates.total_amount) filters.total_amount = filterStates.total_amount
  
  // Add date range filters
  if (filterStates.transaction_date_from || filterStates.transaction_date_to) {
    filters.transaction_date = {
      from: filterStates.transaction_date_from || undefined,
      to: filterStates.transaction_date_to || undefined
    }
  }

  const [limit] = useState<number>(15); // Adjust page size if needed;

  // Hotkey for creating new Payment Out (e.g., Alt+N or Ctrl+N)
  useHotkeys('alt+n, ctrl+n', () => navigate('/payments-out'), { preventDefault: true });

  // Determine sort parameters for API call
  const getSortParams = useCallback(() => {
    if (sorting.length === 0) return { sort_by: 'transaction_date', sort_dir: 'desc' as SortDirection };
    const sort = sorting[0];
    return { sort_by: sort.id, sort_dir: (sort.desc ? 'desc' : 'asc') as SortDirection };
  }, [sorting]);

  // Fetch Payments Out (filtered vouchers)
  const { data: paymentsData, isLoading, isError, error } = useQuery<ExtendedPaginatedResponse<VoucherResponse>, Error>({
    queryKey: ['paymentsOut', page, limit, search, sorting, filters, currentOrganizationId],
    queryFn: () => {
      const { sort_by, sort_dir } = getSortParams();
      const skip = page * limit;

      // Build filters: MUST include voucher_type_id and party_id not null
      const baseFilters: FilterObject[] = [
        { field: 'voucher_type_id', op: 'eq', value: PAYMENT_MADE_VOUCHER_TYPE_ID },
        { field: 'party_id', op: 'isnull', value: false } // Payments should have a party
      ];
      
      // Parse additional filters
      let additionalFilters: FilterObject[] = [];
      if (Object.keys(filters).length > 0) {
        const filterJson = buildFilterParams(filters);
        additionalFilters = JSON.parse(filterJson);
      }
      
      // Combine base and additional filters
      const allFilters = [...baseFilters, ...additionalFilters];

      return listVouchers({
        skip,
        limit,
        search_query: search || undefined,
        sort_by,
        sort_dir,
        filters: JSON.stringify(allFilters), // Send filters as JSON string
      });
    },
    enabled: !!currentOrganizationId, // Only run when organization ID is available
    staleTime: 1 * 60 * 1000, // 1 minute stale time
  });

  // Calculate page count based on backend response variations
  const pageCount = useMemo(() => {
      const total = paymentsData?.total ?? 0;
      return Math.ceil(total / limit);
  }, [paymentsData?.total, limit]);


  // Event handlers
  const handleSearchChange = useCallback((value: string): void => {
    setSearch(value);
    setPage(0); // Reset page on search
  }, []);

  const handleSortingChange = useCallback((newSorting: SortingState): void => {
    setSorting(newSorting);
    setPage(0); // Reset page on sort
  }, []);

  const handlePageChange = useCallback((newPage: number): void => {
    setPage(newPage);
  }, []);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Record<string, unknown>): void => {
    console.log('handleFilterChange called with:', newFilters);
    
    // Build the new filter states for nuqs
    const updates: Record<string, string | string[] | null> = {}
    let hasActualChanges = false

    // List of all possible filter keys that could come from the DataTable
    const knownFilterKeys = ['voucher_number', 'party.name', 'reference_number', 'is_cancelled', 'transaction_date', 'total_amount']
    
    // First, check all known keys and set to null if not present in newFilters
    knownFilterKeys.forEach(key => {
      if (!(key in newFilters)) {
        const mappedKey = key === 'party.name' ? 'party_name' : key
        
        // For date range filters, clear both from and to
        if (key === 'transaction_date') {
          if (filterStates[`${key}_from`] !== null || filterStates[`${key}_to`] !== null) {
            hasActualChanges = true
            updates[`${key}_from`] = null
            updates[`${key}_to`] = null
          }
        } else if (filterStates[mappedKey] !== null) {
          hasActualChanges = true
          updates[mappedKey] = null
        }
      }
    })

    Object.entries(newFilters).forEach(([key, value]) => {
      // Map 'party.name' to 'party_name' for URL compatibility
      const mappedKey = key === 'party.name' ? 'party_name' : key
      
      // Handle date range object
      if (typeof value === 'object' && value !== null && 'from' in value) {
        const dateRange = value as { from?: string; to?: string }
        const fromKey = `${key}_from`
        const toKey = `${key}_to`
        const newFrom = dateRange.from || null
        const newTo = dateRange.to || null
        
        // Check if values actually changed
        if (filterStates[fromKey] !== newFrom || filterStates[toKey] !== newTo) {
          hasActualChanges = true
        }
        
        updates[fromKey] = newFrom
        updates[toKey] = newTo
      } else if (value === undefined || value === null || value === '') {
        // Clear the filter
        if (filterStates[mappedKey] !== null) {
          hasActualChanges = true
        }
        updates[mappedKey] = null
      } else {
        // Handle simple string/number values
        const newValue = String(value)
        if (filterStates[mappedKey] !== newValue) {
          hasActualChanges = true
        }
        updates[mappedKey] = newValue
      }
    })

    console.log('Updating filters with:', updates, 'hasActualChanges:', hasActualChanges)
    console.log('Current filterStates:', filterStates)
    
    // Update filters using nuqs
    setFilterStates(updates)
    
    // Only reset to first page when filters actually change
    if (hasActualChanges) {
      setPage(0)
    }
  }, [setFilterStates, setPage, filterStates]);

  // Define columns
  const columns = useMemo(() => paymentsOutColumns(), []);

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Payments Out</h1>
        <Button asChild>
          <Link to="/payments-out"> {/* Link to create form */}
            <Plus className="h-4 w-4 mr-2" />
            Record Payment Out
          </Link>
        </Button>
      </div>

      {isError && (
        <Alert variant="destructive">
           <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading payments: {(error as Error).message}
          </AlertDescription>
        </Alert>
      )}

      {/* Payments Data Table with filters */}
      <PaymentsOutDataTable
        data={paymentsData?.items || []}
        columns={columns}
        isLoading={isLoading}
        pageCount={pageCount}
        page={page}
        setPage={handlePageChange}
        limit={limit}
        totalRecords={paymentsData?.total ?? 0}
        onSearchChange={handleSearchChange}
        searchValue={search}
        onSortingChange={handleSortingChange}
        sortingState={sorting}
        onFilterChange={handleFilterChange}
        initialFilters={filters}
      />
    </div>
  );
};

export default PaymentsOutListPage;